import * as React from "react";
// Controls
import { DefaultButton, DirectionalHint, Label, useTheme } from "@fluentui/react";
import { ContextualMenu } from "../../../contextualMenu/ContextualMenu";
import { CollapsibleSection } from "../CollapsibleSection";
import { TaskAttachmentList } from "./TaskAttachmentList";
// Constants
import { ControlRenderMode } from "@ms/uno-constants/lib/local/RenderModeConstants";
import { TaskAttachment } from "@ms/uno-models/lib/local/client/taskAttachment/TaskAttachment";
import { TaskAttachmentRowItem } from "@ms/uno-models/lib/local/client/taskAttachment/TaskAttachmentRowItem";
// Styles
import { getClassNames, getStyles } from "./TaskDetailsAttachmentsSection.styles";
// Utilities
import { getStyleObject } from "@ms/uno-utilities/lib/local/StyleUtilities";
import findIndex from "lodash/findIndex";
import forOwn from "lodash/forOwn";
export function TaskDetailsAttachmentsSection(props) {
    const [isAddAttachmentContextMenuOpen, setIsAddAttachmentContextMenuOpen] = React.useState(false);
    const [addAttachmentButton, setAddAttachmentButton] = React.useState(null);
    const fileInputElement = React.useRef(null);
    const addAttachmentContextMenuTarget = React.useRef(null);
    const theme = useTheme();
    const styles = getStyles(props.styles);
    const classNames = getClassNames(styles);
    const { callbacks, config, taskAttachmentRowItems, taskAttachmentSectionStrings, taskAttachmentToEdit, taskAttachmentRowIcons, documentPreviewIcons, renderCollapsibleAttachments, maximumSizeForTaskAttachment, maximumAttachmentsPerTask } = props;
    /**
     * Return the list of task attachment row items sorted by alias
     */ const getAttachmentsSortedByAlias = React.useCallback(()=>{
        const taskAttachmentRowItemsList = [];
        // Map dictionary to a list
        forOwn(taskAttachmentRowItems, (value, key)=>{
            taskAttachmentRowItemsList.push(value);
        });
        // Now sort by alias
        taskAttachmentRowItemsList.sort((item1, item2)=>TaskAttachmentRowItem.aliasCompare(item1, item2));
        return taskAttachmentRowItemsList;
    }, [
        taskAttachmentRowItems
    ]);
    /**
     * Render the task attachments on task details
     */ const renderTaskAttachments = ()=>{
        if (taskAttachmentRowItems.length === 0) {
            return null;
        }
        const attachedDocuments = getAttachmentsSortedByAlias();
        // Set the attachment being edited
        let taskAttachmentToEditIndex = -1;
        let isAttachmentBeingEdited = false;
        if (taskAttachmentToEdit != null) {
            const attachmentUrl = taskAttachmentToEdit.url;
            isAttachmentBeingEdited = true;
            taskAttachmentToEditIndex = findIndex(attachedDocuments, (taskAttachmentRowITem)=>taskAttachmentRowITem.url === attachmentUrl);
        }
        const attachmentListCallbacks = {
            onDeleteTaskAttachment: callbacks.onDeleteTaskAttachment,
            onEditTaskAttachment: callbacks.onOpenTaskAttachmentEditorDialog,
            onSelectDeselectShowOnCard: callbacks.onSelectDeselectShowOnCard,
            onOpenLink: callbacks.onOpenLink,
            onCopyLink: callbacks.onOpenLink
        };
        const attachmentRowConfig = {
            hideOpenInNewWindow: config.hideOpenInNewWindow,
            hideShowOnCardButton: config.hideShowOnCardButton,
            disableEdit: config.disableEditAttachment,
            disableRemove: config.disableRemoveAttachment,
            disableCopyLink: config.disableCopyLink,
            disableActionButton: config.disableActionButton,
            hideMoreButton: config.hideMoreButton,
            disableOpenAttachment: config.disableOpenAttachment,
            disableExtraHostnameUrlValidation: config.disableExtraHostnameUrlValidation
        };
        return /*#__PURE__*/ React.createElement(TaskAttachmentList, {
            altFocusableElement: addAttachmentButton ? addAttachmentButton : undefined,
            taskAttachmentRowItems: attachedDocuments,
            isTaskAttachmentBeingEdited: isAttachmentBeingEdited,
            taskAttachmentToEditIndex: taskAttachmentToEditIndex,
            callbacks: attachmentListCallbacks,
            rowConfig: attachmentRowConfig,
            taskAttachmentRowStrings: taskAttachmentSectionStrings.taskAttachmentRowStrings,
            styles: styles.subComponentStyles.list,
            taskAttachmentRowIcons: taskAttachmentRowIcons,
            documentPreviewIcons: documentPreviewIcons
        });
    };
    /**
     * Callback function to kick off a file upload
     * @param evt The onchange event for the file input element
     */ const uploadFile = React.useCallback((evt)=>{
        if (addAttachmentButton != null) {
            addAttachmentButton.focus();
        }
        setIsAddAttachmentContextMenuOpen(false);
        const element = evt.target;
        if (element.files && element.files.length > 0) {
            const file = element.files[0];
            if (!file) {
                return;
            }
            if (file.size === 0) {
                callbacks.onSelectEmptyFile(file.name.substring(0, TaskAttachment.AliasMaxLength));
                return;
            }
            if (file.size > maximumSizeForTaskAttachment) {
                callbacks.onOpenErrorDialog(taskAttachmentSectionStrings.fileTooBigErrorMessage);
                return;
            }
            callbacks.onUploadFileAsTaskAttachment(file);
        }
        // Reset the file input - see http://stackoverflow.com/questions/12030686/html-input-file-selection-event-not-firing-upon-selecting-the-same-file
        element.value = null;
    }, [
        addAttachmentButton,
        callbacks,
        maximumSizeForTaskAttachment,
        taskAttachmentSectionStrings.fileTooBigErrorMessage
    ]);
    /**
     * Render the add task attachments button on task details
     */ const renderAddAttachmentsButton = ()=>{
        if (config.addAttachmentButtonOption === ControlRenderMode.Hidden) {
            return null;
        }
        const customButtonStyles = getStyleObject(styles.subComponentStyles.addAttachmentButton);
        const maximumAttachmentsAdded = taskAttachmentRowItems.length >= maximumAttachmentsPerTask;
        const disabled = maximumAttachmentsAdded || config.addAttachmentButtonOption === ControlRenderMode.Disabled;
        const hoverText = config.addAttachmentButtonOption === ControlRenderMode.Disabled ? taskAttachmentSectionStrings.noEditPermissionHoverText : maximumAttachmentsAdded ? taskAttachmentSectionStrings.maximumTaskAttachmentsAddedHoverText : "";
        return /*#__PURE__*/ React.createElement("div", {
            className: classNames.addAttachmentButtonWrapper,
            ref: addAttachmentContextMenuTarget
        }, /*#__PURE__*/ React.createElement(DefaultButton, {
            styles: customButtonStyles,
            disabled: disabled,
            onClick: ()=>setIsAddAttachmentContextMenuOpen(!isAddAttachmentContextMenuOpen),
            title: hoverText,
            "aria-label": taskAttachmentSectionStrings.addTaskAttachmentButtonText,
            componentRef: (button)=>setAddAttachmentButton(button)
        }, taskAttachmentSectionStrings.addTaskAttachmentButtonText));
    };
    /**
     * Callback for when the file picker is closed without selecting a file
     */ const onCloseFilePicker = React.useCallback(()=>{
        // Delete the body listener
        document.body.onfocus = null;
        if (addAttachmentButton != null) {
            addAttachmentButton.focus();
        }
        setIsAddAttachmentContextMenuOpen(false);
    }, [
        addAttachmentButton
    ]);
    /**
     * Delegate to open the add link dialog
     * @param elementToFocusOnDismiss The element to restore focus to when the dialog is dismissed
     */ const openAddLinkDialog = React.useCallback((elementToFocusOnDismiss)=>{
        callbacks.onOpenAddLinkDialog(elementToFocusOnDismiss);
    }, [
        callbacks
    ]);
    /**
     * Renders the context menu for the add attachment dialog
     */ const renderAddAttachmentContextMenuToOpen = ()=>{
        if (isAddAttachmentContextMenuOpen) {
            const menuItems = [];
            const isFileUploadDisabled = config.fileUploadOption === ControlRenderMode.Disabled;
            config.fileUploadOption !== ControlRenderMode.Hidden && menuItems.push({
                key: "File",
                name: taskAttachmentSectionStrings.addTaskAttachmentMenuOptionTextFileUpload,
                onClick: ()=>{
                    fileInputElement.current?.click();
                    fileInputElement.current?.focus();
                    // There's no event fired when the file picker is closed without
                    // selecting a file, so keep track with a TEMPORARY listener on the
                    // document body. We need to restore focus when the file picker closes.
                    document.body.onfocus = ()=>onCloseFilePicker();
                },
                disabled: isFileUploadDisabled,
                title: isFileUploadDisabled ? taskAttachmentSectionStrings.addTaskAttachmentMenuOptionFileUploadDisabledTooltip : "",
                ariaLabel: isFileUploadDisabled ? taskAttachmentSectionStrings.addTaskAttachmentMenuOptionFileUploadDisabledTooltip : ""
            });
            const isSharepointDisabled = config.sharepointOption === ControlRenderMode.Disabled;
            config.sharepointOption !== ControlRenderMode.Hidden && menuItems.push({
                key: "Sharepoint",
                name: taskAttachmentSectionStrings.addTaskAttachmentMenuOptionTextSharePoint,
                onClick: callbacks.onOpenOneDrivePicker,
                disabled: isSharepointDisabled,
                title: isSharepointDisabled ? taskAttachmentSectionStrings.addTaskAttachmentMenuOptionSharePointDisabledTooltip : "",
                ariaLabel: isSharepointDisabled ? taskAttachmentSectionStrings.addTaskAttachmentMenuOptionSharePointDisabledTooltip : ""
            });
            menuItems.push({
                key: "Link",
                name: taskAttachmentSectionStrings.addTaskAttachmentMenuOptionTextLink,
                onClick: ()=>openAddLinkDialog(addAttachmentButton)
            });
            return /*#__PURE__*/ React.createElement(ContextualMenu, {
                shouldFocusOnMount: true,
                target: addAttachmentContextMenuTarget,
                onDismiss: ()=>setIsAddAttachmentContextMenuOpen(false),
                directionalHint: DirectionalHint.bottomLeftEdge,
                items: menuItems,
                styles: styles.subComponentStyles.contextualMenu,
                theme: theme
            });
        }
        return null;
    };
    const attachments = props.taskAttachmentRowItems.length === 0 ? /*#__PURE__*/ React.createElement("div", {
        className: classNames.emptyAttachmentsSection
    }, taskAttachmentSectionStrings.taskAttachmentsLabel) : renderCollapsibleAttachments ? /*#__PURE__*/ React.createElement(CollapsibleSection, {
        label: taskAttachmentSectionStrings.taskAttachmentsLabel,
        collapsibleSectionHideAriaLabel: taskAttachmentSectionStrings.collapsibleSectionHideAriaLabel,
        collapsibleSectionShowAriaLabel: taskAttachmentSectionStrings.collapsibleSectionShowAriaLabel,
        styles: styles.subComponentStyles.collapsibleSection
    }, renderTaskAttachments()) : /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(Label, {
        styles: styles.subComponentStyles.attachmentLabel
    }, taskAttachmentSectionStrings.taskAttachmentsLabel), " ", renderTaskAttachments());
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement("input", {
        className: classNames.hiddenFileInput,
        type: "file",
        onChange: uploadFile,
        ref: fileInputElement
    }), attachments, renderAddAttachmentsButton(), renderAddAttachmentContextMenuToOpen());
}
